import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import {
  FormRowLabel,
  FormRowLayout,
  FormRowValue,
} from '@/common/components/FormRow/FormRow.tsx'
import { getTransactionById } from '@/queries/transactions/queries.ts'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import Image from '@/app/common/components/Image'
import { DocumentsViewer } from '@/app/common/components/PaymentElements/DocumentViewer'
import { PurseMovementCard } from '@/app/common/components/PaymentElements/PurseMovementCard'
import TransactionStatusBadge from '@/app/common/components/TransactionStatusBadge'
import { CheckCircleIcon } from '@/app/common/icons/general'
import LoadingCard from '@/common/components/LoadingCard.tsx'
import { currencyFormatter } from '@/lib/utils'
import { RootState } from '@/storage/store.ts'
import { useSelector } from 'react-redux'

export default function TransactionDetailsPage() {
  const { transactionId } = useParams<{ transactionId: string }>()
  const isValidTransactionId = !isNaN(Number(transactionId))
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)

  const {
    data: transaction,
    isLoading,
    isError,
  } = useQuery({
    enabled: isValidTransactionId,
    queryKey: ['transaction', transactionId, selectedProgramYear?.id ?? 0],
    queryFn: () =>
      getTransactionById(Number(transactionId), selectedProgramYear?.id ?? 0),
  })

  if (isLoading) return <LoadingCard title="Loading..." />
  if (isError || !transaction) return <p>Failed to load transaction.</p>

  return (
    <div className="grid grid-cols-2 gap-4">
      <Card>
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Transaction Details</h2>
          <div>
            <TransactionStatusBadge value={transaction.status} />
          </div>
        </CardHeader>
        <CardContent>
          <FormRowLayout>
            <FormRowLabel>Transaction No</FormRowLabel>
            <FormRowValue>{transaction.transaction_id}</FormRowValue>
          </FormRowLayout>

          {transaction.transaction_type === 'Reimbursement' &&
            transaction.parent_id && (
              <FormRowLayout>
                <FormRowLabel>Reimbursed Transaction ID</FormRowLabel>
                <FormRowValue>{transaction.parent_id}</FormRowValue>
              </FormRowLayout>
            )}

          <FormRowLayout>
            <FormRowLabel>Transaction Type</FormRowLabel>
            <FormRowValue>{transaction.transaction_type}</FormRowValue>
          </FormRowLayout>

          <FormRowLayout>
            <FormRowLabel>Provider</FormRowLabel>
            <FormRowValue>
              {transaction.provider_name
                ? transaction.provider_id !== null
                  ? `${transaction.provider_name} (ID: ${transaction.provider_id})`
                  : transaction.provider_name
                : ''}
            </FormRowValue>
          </FormRowLayout>

          <FormRowLayout>
            <FormRowLabel>Applicant</FormRowLabel>
            <FormRowValue>{transaction.applicant_name}</FormRowValue>
          </FormRowLayout>

          <FormRowLayout>
            <FormRowLabel>Created At</FormRowLabel>
            <FormRowValue>
              {new Date(transaction.created_datetime).toLocaleString()}
            </FormRowValue>
          </FormRowLayout>
          <FormRowLayout>
            <FormRowLabel>Invoice Quote</FormRowLabel>
            <FormRowValue>
              {transaction.invoice_quote ?? 'Not added'}
            </FormRowValue>
          </FormRowLayout>
          <FormRowLayout>
            <FormRowLabel>Tracking Number</FormRowLabel>
            <FormRowValue>
              {transaction.tracking_number ?? 'Not added'}
            </FormRowValue>
          </FormRowLayout>
          <FormRowLayout>
            <FormRowLabel>Transaction UUID</FormRowLabel>
            <FormRowValue>{transaction.uuid ?? 'Not available'}</FormRowValue>
          </FormRowLayout>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-bold">Amounts</h2>
        </CardHeader>
        <CardContent>
          <FormRowLayout>
            <FormRowLabel>Total Amount</FormRowLabel>
            <FormRowValue>
              {currencyFormatter.format(transaction.total_amount)}
            </FormRowValue>
          </FormRowLayout>

          <FormRowLayout>
            <FormRowLabel>Service Fee</FormRowLabel>
            <FormRowValue>
              {currencyFormatter.format(transaction.service_fee)}
            </FormRowValue>
          </FormRowLayout>

          <FormRowLayout>
            <FormRowLabel>Amount to Provider</FormRowLabel>
            <FormRowValue>
              {currencyFormatter.format(transaction.amount_provider)}
            </FormRowValue>
          </FormRowLayout>
        </CardContent>
      </Card>

      {Boolean(transaction.has_appealed) &&
        transaction.status === 'Completed' && (
          <Card>
            <CardHeader className="text-lg font-bold space-x-2">
              <CheckCircleIcon className="text-primary mr-2" />
              Approval Reason
            </CardHeader>
            <CardContent>{transaction.last_message}</CardContent>
          </Card>
        )}

      <Card>
        <CardHeader>
          <h2 className="text-lg font-bold">Students</h2>
        </CardHeader>
        <CardContent>
          {transaction.students?.length ? (
            transaction.students.map(
              (s: { student_id: number; student_name: string }) => (
                <FormRowLayout key={s.student_id}>
                  <FormRowLabel>Student</FormRowLabel>
                  <FormRowValue>{s.student_name}</FormRowValue>
                </FormRowLayout>
              )
            )
          ) : (
            <p>No students found.</p>
          )}
        </CardContent>
      </Card>
      {transaction?.payment_items && transaction?.payment_items?.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-bold">Products</h2>
          </CardHeader>
          <CardContent>
            <div className="[&>*:nth-child(odd)]:bg-gray-50 space-y-4">
              {transaction?.payment_items.map((item) => (
                <div className="flex items-center gap-2">
                  <div className="h-14 w-14 flex-shrink-0 relative">
                    <div className="absolute h-5 w-5 bg-gray-100 flex items-center justify-center -top-1 -right-1 z-50 border border-gray-300 rounded-full">
                      {item.quantity}
                    </div>
                    {item.provider_item.image && (
                      <Image
                        className="h-14 w-14 flex-shrink-0 rounded-md object-cover"
                        path={item.provider_item.image}
                      />
                    )}
                  </div>
                  <div>
                    <div className="font-normal text-sm">
                      {item.provider_item.name}
                    </div>
                    <div className="font-normal">
                      <span className="font-bold">
                        {currencyFormatter.format(item.price)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <PurseMovementCard purses={transaction.purse_movements} />

      <DocumentsViewer
        documents={transaction.payment_documents}
        students={transaction.students}
      />

      {transaction.expense_categories &&
        transaction.expense_categories.length > 0 && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-bold">Expense Categories</h2>
            </CardHeader>
            <CardContent>
              {transaction.expense_categories.map((cat) => (
                <FormRowLayout key={cat.id}>
                  <FormRowLabel>{cat.name}</FormRowLabel>
                  <FormRowValue>
                    {currencyFormatter.format(cat.amount)}
                  </FormRowValue>
                </FormRowLayout>
              ))}
            </CardContent>
          </Card>
        )}
    </div>
  )
}
